/**
 * Krok 1: Podstawy firmy — Nazwa + Branża (rozwijane menu z opcją „Dodaj własną") + Lokalizacja.
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Check, X, CheckCircle2, MapPin, Briefcase, ChevronDown, Plus } from 'lucide-react';
import { formatPostalCode, getVoivodeshipFromPostalCode } from '@/utils/postalCode';
import { BUSINESS_INDUSTRIES, INDUSTRY_GROUPS } from '../industries';

const NAME_MIN = 3;
const NAME_MAX = 60;

export default function Step1Basics({ data, updateData, errors = {} }) {
  const [industryQuery, setIndustryQuery] = useState('');
  const [industryOpen, setIndustryOpen] = useState(false);
  const industryBoxRef = useRef(null);

  // Zamykanie rozwijanego menu branży po kliknięciu poza nim
  useEffect(() => {
    if (!industryOpen) return undefined;
    const onDocClick = (e) => {
      if (industryBoxRef.current && !industryBoxRef.current.contains(e.target)) {
        setIndustryOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [industryOpen]);

  const filtered = useMemo(() => {
    const q = industryQuery.trim().toLowerCase();
    if (!q) return BUSINESS_INDUSTRIES;
    return BUSINESS_INDUSTRIES.filter((i) =>
      i.name.toLowerCase().includes(q) || i.slug.includes(q) || i.group.toLowerCase().includes(q)
    );
  }, [industryQuery]);

  const handlePostal = (val) => {
    const formatted = formatPostalCode(val);
    const updates = { postal_code: formatted };
    if (formatted && /^\d{2}-\d{3}$/.test(formatted)) {
      const detected = getVoivodeshipFromPostalCode(formatted);
      if (detected) updates.voivodeship = detected;
    }
    updateData(updates);
  };

  const pickIndustry = (slug) => {
    updateData({ industry: slug });
    setIndustryQuery('');
    setIndustryOpen(false);
  };

  const nameLen = (data.name || '').length;
  const nameValid = nameLen >= NAME_MIN && nameLen <= NAME_MAX;
  const selectedIndustry = data.industry;
  // Fallback na surową wartość → obsługa „własnej branży" spoza listy.
  const selectedIndustryName = BUSINESS_INDUSTRIES.find((i) => i.slug === selectedIndustry)?.name || selectedIndustry;

  // Czy pokazać opcję „Dodaj własną branżę" (gdy wpisana fraza nie pasuje dokładnie do żadnej z listy).
  const q = industryQuery.trim();
  const canAddCustom = q.length >= 2 && !BUSINESS_INDUSTRIES.some((i) => i.name.toLowerCase() === q.toLowerCase());

  return (
    <div className="space-y-8" data-testid="business-wizard-v2-step-basics">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-orange-500" />
          </div>
          <span className="text-xs uppercase tracking-wider font-semibold text-orange-600">Krok 1 z 7</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Podstawy firmy</h2>
        <p className="text-sm text-gray-500 mt-1">Zacznijmy od tego, jak nazywa się Twoja firma i czym się zajmuje.</p>
      </header>

      {/* 1. Nazwa firmy */}
      <section>
        <div className="flex items-start gap-3 mb-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
          <div className="flex-1">
            <Label className="text-base font-semibold text-gray-900">Jak nazywa się Twoja firma?</Label>
            <p className="text-xs text-gray-500 mt-0.5">Pełna nazwa, którą wpiszesz na stronie i wizytówce.</p>
          </div>
        </div>
        <div className="ml-10">
          <Input
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            placeholder="np. Salon fryzjerski Anna Nowak"
            maxLength={NAME_MAX}
            data-testid="bwv2-name-input"
            className={errors.name ? 'border-red-400' : ''}
          />
          <div className="flex items-center justify-between mt-1.5 text-xs">
            <span className={errors.name ? 'text-red-600' : 'text-gray-400'}>
              {errors.name || `min. ${NAME_MIN} znaki`}
            </span>
            <span className={`${nameValid ? 'text-emerald-600' : 'text-gray-400'}`}>
              {nameLen}/{NAME_MAX}
            </span>
          </div>
        </div>
      </section>

      {/* 2. Branża — rozwijane menu (oszczędza miejsce) */}
      <section>
        <div className="flex items-start gap-3 mb-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
          <div className="flex-1">
            <Label className="text-base font-semibold text-gray-900">W jakiej branży działasz?</Label>
            <p className="text-xs text-gray-500 mt-0.5">Wybierz z listy lub dodaj własną — od tego zależy w jakich katalogach Cię znajdziemy.</p>
          </div>
        </div>
        <div className="ml-10">
          <div className="relative" ref={industryBoxRef}>
            {/* Trigger rozwijanego menu */}
            <button
              type="button"
              onClick={() => setIndustryOpen((o) => !o)}
              data-testid="bwv2-industry-trigger"
              className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg border bg-white text-left transition-all ${
                errors.industry
                  ? 'border-red-400'
                  : industryOpen
                    ? 'border-orange-400 ring-2 ring-orange-100'
                    : 'border-gray-200 hover:border-orange-300'
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <Briefcase className={`w-4 h-4 flex-shrink-0 ${selectedIndustryName ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className={`truncate text-sm ${selectedIndustryName ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                  {selectedIndustryName || 'Dodaj branżę'}
                </span>
              </span>
              <span className="flex items-center gap-1 flex-shrink-0">
                {selectedIndustryName && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); updateData({ industry: '' }); }}
                    data-testid="bwv2-industry-clear"
                    className="hover:bg-gray-100 rounded-full p-0.5 text-gray-400 hover:text-gray-600"
                    aria-label="Wyczyść branżę"
                  >
                    <X className="w-3.5 h-3.5" />
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${industryOpen ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {/* Panel rozwijany */}
            {industryOpen && (
              <div
                className="absolute z-30 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-xl"
                data-testid="bwv2-industry-dropdown"
              >
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={industryQuery}
                      onChange={(e) => setIndustryQuery(e.target.value)}
                      placeholder="Szukaj lub wpisz własną branżę..."
                      className="pl-9"
                      data-testid="bwv2-industry-search"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto p-2 space-y-3">
                  {INDUSTRY_GROUPS.map((group) => {
                    const items = filtered.filter((i) => i.group === group);
                    if (items.length === 0) return null;
                    return (
                      <div key={group}>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1.5 px-1">{group}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {items.map((ind) => {
                            const active = selectedIndustry === ind.slug;
                            return (
                              <button
                                key={ind.slug}
                                type="button"
                                onClick={() => pickIndustry(ind.slug)}
                                data-testid={`bwv2-industry-${ind.slug}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                  active
                                    ? 'bg-orange-500 border-orange-500 text-white shadow-sm'
                                    : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                                }`}
                              >
                                {active && <Check className="w-3 h-3 inline mr-1" />}
                                {ind.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Dodaj własną branżę spoza listy */}
                  {canAddCustom && (
                    <button
                      type="button"
                      onClick={() => pickIndustry(q)}
                      data-testid="bwv2-industry-add-custom"
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-orange-300 text-orange-700 hover:bg-orange-50 text-sm font-medium"
                    >
                      <Plus className="w-4 h-4 flex-shrink-0" /> Dodaj „{q}"
                    </button>
                  )}

                  {filtered.length === 0 && !canAddCustom && (
                    <p className="text-sm text-gray-400 italic px-1 py-2">Zacznij pisać, aby znaleźć lub dodać branżę.</p>
                  )}
                </div>
              </div>
            )}
          </div>
          {errors.industry && <p className="text-xs text-red-600 mt-1">{errors.industry}</p>}
        </div>
      </section>

      {/* 3. Lokalizacja */}
      <section>
        <div className="flex items-start gap-3 mb-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
          <div className="flex-1">
            <Label className="text-base font-semibold text-gray-900">Gdzie świadczysz usługi?</Label>
            <p className="text-xs text-gray-500 mt-0.5">Miasto pojawi się w SEO oraz w katalogu lokalnym.</p>
          </div>
        </div>
        <div className="ml-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Miasto *</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={data.city || ''}
                onChange={(e) => updateData({ city: e.target.value })}
                placeholder="np. Warszawa"
                className={`pl-9 ${errors.city ? 'border-red-400' : ''}`}
                data-testid="bwv2-city-input"
              />
            </div>
            {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
          </div>
          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Kod pocztowy</Label>
            <Input
              value={data.postal_code || ''}
              onChange={(e) => handlePostal(e.target.value)}
              placeholder="00-001"
              maxLength={6}
              data-testid="bwv2-postal-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-gray-600 mb-1 block">Adres (opcjonalnie)</Label>
            <Input
              value={data.address || ''}
              onChange={(e) => updateData({ address: e.target.value })}
              placeholder="np. ul. Marszałkowska 1"
              data-testid="bwv2-address-input"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
